import * as Sentry from '@sentry/browser'

export class UploadHttpError extends Error {
  readonly status: number

  constructor(status: number) {
    super(`HTTP Error: ${status}`)
    this.name = 'UploadHttpError'
    this.status = status
  }
}

export class UploadNetworkError extends Error {
  constructor() {
    super('Network Error')
    this.name = 'UploadNetworkError'
  }
}

export interface UploadFileOptions {
  url: string
  file: File
  signal?: AbortSignal
  /**
   *
   * @param progress 0 to 100, so treat it like a percentage
   * @param file reference to the same file passed in
   * @returns void
   */
  onProgress?: (progress: number, file: File) => void
  headers?: Record<string, string>
}

function hostOf(url: string): string {
  try {
    return new URL(url, window.location.href).host
  } catch {
    return 'invalid-url'
  }
}

export function uploadFile({
  url,
  file,
  signal,
  onProgress,
  headers = {},
}: UploadFileOptions): Promise<void> {
  // oxlint-disable-next-line promise/avoid-new
  return new Promise<void>((resolve, reject) => {
    // eslint-disable-next-line cf-web/prefer-utils-over-xhr
    const xhr = new XMLHttpRequest()

    if (signal?.aborted) {
      reject(new DOMException('Upload Aborted', 'AbortError'))
      return
    }

    const abortRequest = () => xhr.abort()
    signal?.addEventListener('abort', abortRequest)
    xhr.onloadend = () => signal?.removeEventListener('abort', abortRequest)

    if (onProgress) {
      xhr.upload.onprogress = (event: ProgressEvent) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100)
          onProgress(progress, file)
        }
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new UploadHttpError(xhr.status))
      }
    }

    xhr.onerror = () => {
      Sentry.addBreadcrumb({
        category: 'upload',
        level: 'error',
        message: 'Upload request failed',
        data: {
          host: hostOf(url),
          online: navigator.onLine,
          status: xhr.status,
        },
      })
      reject(new UploadNetworkError())
    }

    xhr.onabort = () => {
      reject(new DOMException('Upload Aborted', 'AbortError'))
    }

    xhr.open('PUT', url)

    xhr.setRequestHeader('Content-Type', file.type)

    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value)
    })

    xhr.send(file)
  })
}
